import React from 'react';
import { normalizeWallet } from '../formatting';
import {
    canCompanyReclaimUnacceptedEscrow,
    canEmployeeClaimJobPayment,
    canEmployeeSubmitWork,
    canRaiseJobDispute,
    DEFAULT_ESCROW_RECLAIM_DELAY_SECONDS,
    formatReclaimCountdown,
    formatReclaimDelayLabel,
    getJobReviewClaimableAt,
    isEscrowAcceptedOnChain,
    resolveTaskStatus,
} from '../helpers';

/** Employee refund response — singular; avoid ambiguous "Approve Refunds". */
const EMPLOYEE_ACCEPT_REFUND_LABEL = 'Accept refund';
const EMPLOYEE_REJECT_REFUND_LABEL = 'Reject refund';

const formatClaimCountdown = (claimableAt) => {
    if (!claimableAt) return null;
    const remaining = Number(claimableAt) - Math.floor(Date.now() / 1000);
    if (remaining <= 0) return null;
    const days = Math.floor(remaining / 86400);
    const hours = Math.floor((remaining % 86400) / 3600);
    if (days > 0) return `${days}d ${hours}h`;
    const mins = Math.floor((remaining % 3600) / 60);
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
};

const getButtonStyle = (variant) => {
    switch (variant) {
        case 'primary':
            return 'bg-teal-700 text-white shadow-sm hover:bg-teal-800 hover:shadow-md dark:bg-teal-600 dark:hover:bg-teal-500 font-semibold';
        case 'success':
            return 'border border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 hover:border-emerald-300 hover:shadow-sm dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300 dark:hover:bg-emerald-500/20 dark:hover:border-emerald-500/50';
        case 'danger':
            return 'border border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100 hover:border-rose-300 hover:shadow-sm dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300 dark:hover:bg-rose-500/20 dark:hover:border-rose-500/50';
        case 'warning':
            return 'border border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100 hover:border-amber-300 hover:shadow-sm dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100 dark:hover:bg-amber-500/20 dark:hover:border-amber-500/50';
        case 'ghost':
        default:
            return 'border border-zinc-300 bg-zinc-100 text-zinc-800 hover:border-zinc-400 hover:bg-zinc-200 hover:shadow-sm dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-200 dark:hover:border-slate-600 dark:hover:bg-slate-800/70';
    }
};

const ActionButton = ({
    label,
    onClick,
    actionKey,
    activeAction,
    variant = 'ghost',
}) => {
    const isLoading = activeAction === actionKey;

    return (
        <button
            onClick={onClick}
            disabled={isLoading}
            className={`
                inline-flex items-center justify-center
                px-3 py-2 rounded-lg text-xs font-medium
                transition-all duration-150
                disabled:opacity-50 disabled:cursor-not-allowed
                ${getButtonStyle(variant)}
            `}
        >
            {isLoading ? (
                <span className="animate-pulse">Processing...</span>
            ) : (
                label
            )}
        </button>
    );
};

const PageActionButton = ({
    label,
    onClick,
    actionKey,
    activeAction,
    variant = 'ghost',
    icon,
    fullWidth = false,
}) => {
    const isLoading = activeAction === actionKey;
    const base =
        'inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all duration-150 disabled:cursor-not-allowed disabled:opacity-50';
    const variants = {
        primary:
            'bg-teal-700 text-white shadow-sm hover:bg-teal-800 hover:shadow-md dark:bg-teal-600 dark:hover:bg-teal-500',
        success:
            'bg-emerald-600 text-white shadow-sm hover:bg-emerald-700 hover:shadow-md dark:bg-emerald-600 dark:hover:bg-emerald-500',
        danger:
            'border border-rose-200 bg-rose-50 text-rose-800 hover:border-rose-300 hover:bg-rose-100 hover:shadow-sm dark:border-rose-500/35 dark:bg-rose-500/10 dark:text-rose-200 dark:hover:border-rose-500/50 dark:hover:bg-rose-500/20',
        warning:
            'border border-amber-200 bg-amber-50 text-amber-900 hover:border-amber-300 hover:bg-amber-100 hover:shadow-sm dark:border-amber-500/35 dark:bg-amber-500/10 dark:text-amber-100 dark:hover:border-amber-500/50 dark:hover:bg-amber-500/20',
        ghost:
            'border border-zinc-200 bg-zinc-50 text-zinc-800 hover:border-zinc-300 hover:bg-zinc-100 hover:shadow-sm dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:border-zinc-500 dark:hover:bg-zinc-800',
    };

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={isLoading}
            className={`${base} ${variants[variant] || variants.ghost} ${fullWidth ? 'w-full' : ''}`}
        >
            {icon ? <i className={`${icon} text-xs`} /> : null}
            {isLoading ? 'Processing…' : label}
        </button>
    );
};

const TaskActionButtons = ({
    task,
    normalizedWallet,
    isAdmin,
    activeAction,
    onTaskAction,
    layout = 'inline',
    showSubmitForm = false,
    acceptReclaimDelaySeconds = DEFAULT_ESCROW_RECLAIM_DELAY_SECONDS,
}) => {
    const companyOwns =
        normalizeWallet(task.company?.ownerWallet) === normalizedWallet;

    const employeeOwns =
        normalizeWallet(task.employee?.wallet) === normalizedWallet;

    const resolvedStatus = resolveTaskStatus(task);
    const actionKeyBase = `task-${task.id}`;

    const submissions = Array.isArray(task.submissions) ? [...task.submissions] : [];
    const latestSubmission =
        submissions.sort(
            (a, b) => new Date(b.submittedAt) - new Date(a.submittedAt),
        )[0] || null;

    const isSubmitted = resolvedStatus === 'submitted' || Boolean(latestSubmission);
    const isReviewPending = resolvedStatus === 'submitted';
    const isTaskFinalized =
        ['approved', 'released', 'disputed', 'refunded'].includes(
            resolvedStatus,
        ) || task?.onChain?.cancelled;
    const canRemoveTask =
        (companyOwns || isAdmin) &&
        !task.escrowId &&
        !isSubmitted &&
        !['approved', 'disputed', 'refunded', 'refund_requested'].includes(
            resolvedStatus,
        );

    const chainAccepted = isEscrowAcceptedOnChain(task);
    const employeeCanClaim = canEmployeeClaimJobPayment(task, normalizedWallet);
    const claimableAt = getJobReviewClaimableAt(task);
    const claimCountdown = formatClaimCountdown(claimableAt);
    const canWithdrawUnaccepted = canCompanyReclaimUnacceptedEscrow(
        task,
        acceptReclaimDelaySeconds,
    );
    const reclaimCountdown = formatReclaimCountdown(task, acceptReclaimDelaySeconds);
    const showEmployeeOnChainConfirm =
        employeeOwns &&
        task.escrowId &&
        chainAccepted !== true &&
        resolvedStatus !== 'pending' &&
        !['released', 'disputed', 'refunded'].includes(resolvedStatus);

    /** No on-chain reject — hide for admin and funded escrow tasks. */
    const showRejectTask =
        companyOwns &&
        !isAdmin &&
        isReviewPending &&
        !isTaskFinalized &&
        !task.escrowId;
    const showReworkTask =
        companyOwns &&
        !isAdmin &&
        isReviewPending &&
        !isTaskFinalized &&
        Boolean(task.escrowId) &&
        !employeeCanClaim;

    if (layout === 'page') {
        const pageActions = [];
        const secondary = [];

        if (companyOwns && !task.escrowId) {
            pageActions.push(
                <PageActionButton
                    key="fund"
                    label="Fund escrow"
                    icon="fa-solid fa-wallet"
                    variant="primary"
                    fullWidth
                    onClick={() => onTaskAction(task, 'fund-escrow')}
                    actionKey={`${actionKeyBase}-fund-escrow`}
                    activeAction={activeAction}
                />,
            );
        }
        if (employeeOwns && resolvedStatus === 'pending' && task.escrowId) {
            pageActions.push(
                <PageActionButton
                    key="accept"
                    label="Accept task"
                    variant="primary"
                    fullWidth
                    onClick={() => onTaskAction(task, 'accept-task')}
                    actionKey={`${actionKeyBase}-accept-task`}
                    activeAction={activeAction}
                />,
            );
        }
        if (showEmployeeOnChainConfirm) {
            pageActions.push(
                <PageActionButton
                    key="onchain"
                    label="Confirm on-chain escrow"
                    variant="warning"
                    fullWidth
                    onClick={() => onTaskAction(task, 'accept-escrow-onchain')}
                    actionKey={`${actionKeyBase}-accept-escrow-onchain`}
                    activeAction={activeAction}
                />,
            );
        }
        if (
            !showSubmitForm &&
            canEmployeeSubmitWork(task, normalizedWallet)
        ) {
            pageActions.push(
                <PageActionButton
                    key="submit"
                    label="Submit work"
                    variant="primary"
                    fullWidth
                    onClick={() => onTaskAction(task, 'submit-task')}
                    actionKey={`${actionKeyBase}-submit-task`}
                    activeAction={activeAction}
                />,
            );
        }
        if ((companyOwns || isAdmin) && isReviewPending && task.escrowId && !isTaskFinalized) {
            pageActions.push(
                <PageActionButton
                    key="release"
                    label="Approve & release"
                    icon="fa-solid fa-wallet"
                    variant="success"
                    fullWidth
                    onClick={() => onTaskAction(task, 'complete-task')}
                    actionKey={`${actionKeyBase}-complete-task`}
                    activeAction={activeAction}
                />,
            );
        }
        if (employeeCanClaim) {
            pageActions.push(
                <PageActionButton
                    key="claim-payment"
                    label="Claim payment"
                    icon="fa-solid fa-sack-dollar"
                    variant="success"
                    fullWidth
                    onClick={() => onTaskAction(task, 'claim-payment')}
                    actionKey={`${actionKeyBase}-claim-payment`}
                    activeAction={activeAction}
                />,
            );
        }
        if (showRejectTask) {
            secondary.push(
                <PageActionButton
                    key="reject"
                    label="Reject"
                    icon="fa-solid fa-xmark"
                    variant="danger"
                    onClick={() => onTaskAction(task, 'reject-task')}
                    actionKey={`${actionKeyBase}-reject-task`}
                    activeAction={activeAction}
                />,
            );
        }
        if (showReworkTask) {
            secondary.push(
                <PageActionButton
                    key="rework"
                    label="Request rework"
                    icon="fa-solid fa-rotate"
                    variant="warning"
                    onClick={() => onTaskAction(task, 'request-rework')}
                    actionKey={`${actionKeyBase}-request-rework`}
                    activeAction={activeAction}
                />,
            );
        }
        if (
            (companyOwns || employeeOwns) &&
            canRaiseJobDispute(task)
        ) {
            secondary.push(
                <PageActionButton
                    key="dispute"
                    label="Dispute"
                    icon="fa-solid fa-gavel"
                    variant="danger"
                    onClick={() => onTaskAction(task, 'raise-dispute')}
                    actionKey={`${actionKeyBase}-raise-dispute`}
                    activeAction={activeAction}
                />,
            );
        }
        if (companyOwns && task.escrowId && canWithdrawUnaccepted) {
            secondary.push(
                <PageActionButton
                    key="withdraw-escrow"
                    label="Withdraw escrow"
                    icon="fa-solid fa-rotate-left"
                    variant="warning"
                    onClick={() => onTaskAction(task, 'cancel-escrow')}
                    actionKey={`${actionKeyBase}-cancel-escrow`}
                    activeAction={activeAction}
                />,
            );
        }
        if (
            companyOwns &&
            task.escrowId &&
            chainAccepted === true &&
            ['active', 'submitted'].includes(resolvedStatus)
        ) {
            secondary.push(
                <PageActionButton
                    key="refund"
                    label="Request refund"
                    variant="ghost"
                    onClick={() => onTaskAction(task, 'request-refund')}
                    actionKey={`${actionKeyBase}-request-refund`}
                    activeAction={activeAction}
                />,
            );
        }
        if (employeeOwns && resolvedStatus === 'refund_requested' && task.escrowId) {
            pageActions.push(
                <PageActionButton
                    key="approve-refund"
                    label={EMPLOYEE_ACCEPT_REFUND_LABEL}
                    icon="fa-solid fa-check"
                    variant="success"
                    fullWidth
                    onClick={() => onTaskAction(task, 'approve-refund')}
                    actionKey={`${actionKeyBase}-approve-refund`}
                    activeAction={activeAction}
                />,
            );
            secondary.push(
                <PageActionButton
                    key="reject-refund"
                    label={EMPLOYEE_REJECT_REFUND_LABEL}
                    icon="fa-solid fa-xmark"
                    variant="danger"
                    onClick={() => onTaskAction(task, 'reject-refund')}
                    actionKey={`${actionKeyBase}-reject-refund`}
                    activeAction={activeAction}
                />,
            );
        }

        return (
            <div className="space-y-2">
                {pageActions}
                {secondary.length ? (
                    <div
                        className={`grid gap-2 ${secondary.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}
                    >
                        {secondary}
                    </div>
                ) : null}
                {employeeOwns && resolvedStatus === 'submitted' ? (
                    <p className="py-2 text-center text-xs text-zinc-500">
                        {employeeCanClaim
                            ? 'Review window expired — payment can be claimed.'
                            : claimCountdown
                              ? `Awaiting company review · claim in ${claimCountdown}`
                              : 'Awaiting company review'}
                    </p>
                ) : null}
                {companyOwns &&
                task.escrowId &&
                chainAccepted !== true &&
                !canWithdrawUnaccepted &&
                reclaimCountdown ? (
                    <p className="py-2 text-center text-xs text-amber-800 dark:text-amber-200/90">
                        Withdraw opens in {reclaimCountdown} if the employee has not accepted (
                        {formatReclaimDelayLabel(acceptReclaimDelaySeconds)} window).
                    </p>
                ) : null}
            </div>
        );
    }

    return (
        <div className="flex flex-wrap gap-2 mt-2">

            {/* Primary Actions */}
            {companyOwns && !task.escrowId && (
                <ActionButton
                    label="Fund Escrow"
                    onClick={() => onTaskAction(task, 'fund-escrow')}
                    actionKey={`${actionKeyBase}-fund-escrow`}
                    activeAction={activeAction}
                    variant="primary"
                />
            )}

            {employeeOwns && resolvedStatus === 'pending' && task.escrowId && (
                <ActionButton
                    label="Accept"
                    onClick={() => onTaskAction(task, 'accept-task')}
                    actionKey={`${actionKeyBase}-accept-task`}
                    activeAction={activeAction}
                    variant="primary"
                />
            )}

            {showEmployeeOnChainConfirm && (
                <ActionButton
                    label="Confirm on-chain escrow"
                    onClick={() =>
                        onTaskAction(task, 'accept-escrow-onchain')
                    }
                    actionKey={`${actionKeyBase}-accept-escrow-onchain`}
                    activeAction={activeAction}
                    variant="warning"
                />
            )}

            {!showSubmitForm && canEmployeeSubmitWork(task, normalizedWallet) && (
                    <ActionButton
                        label="Submit"
                        onClick={() => onTaskAction(task, 'submit-task')}
                        actionKey={`${actionKeyBase}-submit-task`}
                        activeAction={activeAction}
                        variant="primary"
                    />
                )}

            {/* Status */}
            {employeeOwns && resolvedStatus === 'submitted' && (
                <span className="rounded-lg border border-zinc-300 bg-zinc-100 px-3 py-2 text-xs text-zinc-700 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-300">
                    {employeeCanClaim
                        ? 'Claim available'
                        : claimCountdown
                          ? `Claim in ${claimCountdown}`
                          : 'Awaiting review'}
                </span>
            )}

            {/* Success */}
            {(companyOwns || isAdmin) &&
                isReviewPending &&
                task.escrowId &&
                !isTaskFinalized && (
                <ActionButton
                    label="Release"
                    onClick={() => onTaskAction(task, 'complete-task')}
                    actionKey={`${actionKeyBase}-complete-task`}
                    activeAction={activeAction}
                    variant="success"
                />
            )}

            {employeeCanClaim && (
                <ActionButton
                    label="Claim payment"
                    onClick={() => onTaskAction(task, 'claim-payment')}
                    actionKey={`${actionKeyBase}-claim-payment`}
                    activeAction={activeAction}
                    variant="success"
                />
            )}

            {/* Danger — off-chain only; not available for escrow / admin */}
            {showRejectTask ? (
                <ActionButton
                    label="Reject"
                    onClick={() => onTaskAction(task, 'reject-task')}
                    actionKey={`${actionKeyBase}-reject-task`}
                    activeAction={activeAction}
                    variant="danger"
                />
            ) : null}

            {showReworkTask ? (
                <ActionButton
                    label="Request rework"
                    onClick={() => onTaskAction(task, 'request-rework')}
                    actionKey={`${actionKeyBase}-request-rework`}
                    activeAction={activeAction}
                    variant="warning"
                />
            ) : null}

            {/* Warning */}
            {companyOwns && task.escrowId && canWithdrawUnaccepted && (
                <ActionButton
                    label="Withdraw escrow"
                    onClick={() => onTaskAction(task, 'cancel-escrow')}
                    actionKey={`${actionKeyBase}-cancel-escrow`}
                    activeAction={activeAction}
                    variant="warning"
                />
            )}

            {companyOwns &&
                task.escrowId &&
                chainAccepted === true &&
                ['active', 'submitted'].includes(resolvedStatus) && (
                    <ActionButton
                        label="Request refund"
                        onClick={() => onTaskAction(task, 'request-refund')}
                        actionKey={`${actionKeyBase}-request-refund`}
                        activeAction={activeAction}
                        variant="warning"
                    />
                )}

            {companyOwns &&
                task.escrowId &&
                chainAccepted !== true &&
                !canWithdrawUnaccepted &&
                reclaimCountdown ? (
                <span className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
                    Withdraw in {reclaimCountdown}
                </span>
            ) : null}

            {employeeOwns &&
                resolvedStatus === 'refund_requested' &&
                task.escrowId && (
                    <>
                        <ActionButton
                            label={EMPLOYEE_ACCEPT_REFUND_LABEL}
                            onClick={() => onTaskAction(task, 'approve-refund')}
                            actionKey={`${actionKeyBase}-approve-refund`}
                            activeAction={activeAction}
                            variant="success"
                        />
                        <ActionButton
                            label={EMPLOYEE_REJECT_REFUND_LABEL}
                            onClick={() => onTaskAction(task, 'reject-refund')}
                            actionKey={`${actionKeyBase}-reject-refund`}
                            activeAction={activeAction}
                            variant="danger"
                        />
                    </>
                )}

            {/* Dispute — only after freelancer has accepted */}
            {(companyOwns || employeeOwns) && canRaiseJobDispute(task) && (
                    <ActionButton
                        label="Dispute"
                        onClick={() => onTaskAction(task, 'raise-dispute')}
                        actionKey={`${actionKeyBase}-raise-dispute`}
                        activeAction={activeAction}
                        variant="warning"
                    />
                )}

            {/* Admin */}
            {isAdmin &&
                resolvedStatus === 'disputed' &&
                task.escrowId && (
                    <>
                        <ActionButton
                            label="Resolve → Employee"
                            onClick={() => onTaskAction(task, 'resolve-employee')}
                            actionKey={`${actionKeyBase}-resolve-employee`}
                            activeAction={activeAction}
                            variant="primary"
                        />
                        <ActionButton
                            label="Resolve → Company"
                            onClick={() => onTaskAction(task, 'resolve-company')}
                            actionKey={`${actionKeyBase}-resolve-company`}
                            activeAction={activeAction}
                            variant="ghost"
                        />
                    </>
                )}
            {canRemoveTask && (
                <ActionButton
                    label="Remove Task"
                    onClick={() => onTaskAction(task, 'delete-task')}
                    actionKey={`${actionKeyBase}-delete-task`}
                    activeAction={activeAction}
                    variant="danger"
                />
            )}
        </div>
    );
};

export default TaskActionButtons;